import React from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { styled } from '@mui/system';
import { IconButton } from '@mui/material';
import AmbTooltip from '../../common/AmbTooltip/AmbTooltip';
import { drawerWidth, toolbarHeight } from '../../../theme';

const MagicWandIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M16.15 13.05L14 16.5C13.8167 16.7833 13.5625 16.9 13.2375 16.85C12.9125 16.8 12.7083 16.6083 12.625 16.275L11.925 13.475L5.10001 20.3C4.91668 20.4833 4.68751 20.5792 4.41251 20.5875C4.13751 20.5958 3.90001 20.5 3.70001 20.3C3.51668 20.1167 3.42501 19.8833 3.42501 19.6C3.42501 19.3167 3.51668 19.0833 3.70001 18.9L10.525 12.05L7.72501 11.35C7.39168 11.2667 7.20001 11.0625 7.15001 10.7375C7.10001 10.4125 7.21668 10.1583 7.50001 9.975L10.95 7.85L10.65 3.775C10.6167 3.44167 10.75 3.2 11.05 3.05C11.35 2.9 11.625 2.93333 11.875 3.15L15 5.775L18.775 4.25C19.0917 4.11667 19.3667 4.16667 19.6 4.4C19.8333 4.63333 19.8833 4.90833 19.75 5.225L18.225 9L20.85 12.1C21.0667 12.35 21.1 12.625 20.95 12.925C20.8 13.225 20.5583 13.3583 20.225 13.325L16.15 13.05ZM3.35001 6.35C3.25001 6.25 3.20001 6.13333 3.20001 6C3.20001 5.86667 3.25001 5.75 3.35001 5.65L4.65001 4.35C4.75001 4.25 4.86668 4.2 5.00001 4.2C5.13335 4.2 5.25001 4.25 5.35001 4.35L6.65001 5.65C6.75001 5.75 6.80001 5.86667 6.80001 6C6.80001 6.13333 6.75001 6.25 6.65001 6.35L5.35001 7.65C5.25001 7.75 5.13335 7.8 5.00001 7.8C4.86668 7.8 4.75001 7.75 4.65001 7.65L3.35001 6.35ZM13.875 12.925L15.075 10.95L17.4 11.125L15.9 9.35L16.775 7.2L14.625 8.075L12.85 6.6L13.025 8.9L11.05 10.125L13.3 10.675L13.875 12.925ZM17.65 20.65L16.35 19.35C16.25 19.25 16.2 19.1333 16.2 19C16.2 18.8667 16.25 18.75 16.35 18.65L17.65 17.35C17.75 17.25 17.8667 17.2 18 17.2C18.1333 17.2 18.25 17.25 18.35 17.35L19.65 18.65C19.75 18.75 19.8 18.8667 19.8 19C19.8 19.1333 19.75 19.25 19.65 19.35L18.35 20.65C18.25 20.75 18.1333 20.8 18 20.8C17.8667 20.8 17.75 20.75 17.65 20.65Z"
      fill="white"
    />
  </svg>
);

const StyledTooltipAnchor = styled('span')({
  position: 'fixed',
  left: drawerWidth,
  top: toolbarHeight + 25,
  zIndex: 10,
  display: 'inline-flex',
});

const StyledIconButton = styled(IconButton)(({ theme }) => ({
  width: '32px',
  height: '32px',
  padding: '4px',
  borderRadius: '0 4px 4px 0',
  background: '#102E4C',
  boxShadow: '0 4px 8px 0 rgba(0, 0, 0, 0.10)',
  zIndex: 10,
  '&:hover': {
    backgroundColor: '#102E4C',
    boxShadow: '0px 4px 8px 0px rgba(0, 0, 0, 0.10)',
  },
  '&:disabled': {
    backgroundColor: '#102E4C',
    opacity: 0.5,
  },
}));

const StyledStatusButton = styled(IconButton)(({ theme }) => ({
  position: 'fixed',
  left: drawerWidth,
  top: toolbarHeight + 25,
  width: 'auto',
  height: '32px',
  padding: '4px 8px 4px 4px',
  display: 'flex',
  gap: '8px',
  alignItems: 'center',
  borderRadius: '0 4px 4px 0',
  background: '#102E4C',
  boxShadow: '0 4px 8px 0 rgba(0, 0, 0, 0.10)',
  '&:hover': {
    backgroundColor: '#102E4C',
    boxShadow: '0px 4px 8px 0px rgba(0, 0, 0, 0.10)',
  },
  '&:disabled': {
    backgroundColor: '#102E4C',
    opacity: 0.5,
  },
}));

const StatusText = styled('span')({
  fontSize: '12px',
  fontWeight: 400,
  whiteSpace: 'nowrap',
  color: 'white',
});

function EligibilityAgentButton({ onClick, disabled = false, isHidden = false, tooltipTitle = '', statusText = null }) {
  if (isHidden) return null;

  const buttonContent = statusText ? (
    <AmbTooltip title={tooltipTitle}>
      <StyledTooltipAnchor>
        <StyledStatusButton onClick={onClick} disabled={disabled} aria-label={statusText}>
          <MagicWandIcon />
          <StatusText>{statusText}</StatusText>
        </StyledStatusButton>
        </StyledTooltipAnchor>
    </AmbTooltip>
  ) : (
    <AmbTooltip title={tooltipTitle}>
      <StyledTooltipAnchor>
        <StyledIconButton onClick={onClick} disabled={disabled} aria-label="Check eligibility with AI agent">
          <MagicWandIcon />
        </StyledIconButton>
      </StyledTooltipAnchor>
    </AmbTooltip>
  );

  return ReactDOM.createPortal(buttonContent, document.body);
}

EligibilityAgentButton.propTypes = {
  onClick: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  isHidden: PropTypes.bool,
  tooltipTitle: PropTypes.string,
  statusText: PropTypes.string,
};

export default EligibilityAgentButton;
